import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { Radio, Calendar, ArrowRight } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;
const POLL_INTERVAL_MS = 30_000;

const formatCountdown = (msUntil) => {
  if (msUntil <= 0) return null;
  const secs = Math.floor(msUntil / 1000);
  const days = Math.floor(secs / 86400);
  const hours = Math.floor((secs % 86400) / 3600);
  const minutes = Math.floor((secs % 3600) / 60);
  const remSecs = secs % 60;
  if (days > 0) return `${days}d ${hours}h ${minutes}m`;
  if (hours > 0) return `${hours}h ${minutes}m`;
  if (minutes > 0) return `${minutes}m ${remSecs}s`;
  return `${remSecs}s`;
};

/**
 * Public banner that shows:
 *  - a "LIVE NOW" CTA when a stream is live
 *  - a countdown when an upcoming stream is within `windowHours` hours
 *  - nothing otherwise
 */
const LiveStreamBanner = ({ windowHours = 24, onVisibilityChange }) => {
  const [data, setData] = useState({ state: 'none', stream: null });
  const [, setTick] = useState(0);

  useEffect(() => {
    const fetchOnce = async () => {
      try {
        const res = await axios.get(`${API}/api/livestream/current`);
        setData(res.data || { state: 'none', stream: null });
      } catch (err) {
        // Non-blocking poll: log and keep the previous state so the banner doesn't flicker on transient errors.
        console.debug('[LiveStreamBanner] poll failed:', err?.message || err);
      }
    };
    fetchOnce();
    const a = setInterval(fetchOnce, POLL_INTERVAL_MS);
    const b = setInterval(() => setTick((x) => x + 1), 1000);
    return () => { clearInterval(a); clearInterval(b); };
  }, []);

  const { state, stream } = data;

  // Determine visibility for parent layout adjustments
  let visible = false;
  if (stream && state === 'live') visible = true;
  if (stream && state === 'upcoming' && stream.scheduled_at) {
    const ms = new Date(stream.scheduled_at).getTime() - Date.now();
    if (ms > 0 && ms <= windowHours * 3600_000) visible = true;
  }

  useEffect(() => {
    onVisibilityChange?.(visible);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visible]);

  if (!visible) return null;

  if (state === 'live') {
    return (
      <Link
        to="/watch-live"
        className="block bg-red-600 text-white hover:bg-red-700 transition shadow-md"
        data-testid="home-live-banner"
      >
        <div className="container mx-auto px-4 py-3 flex items-center justify-center gap-3 flex-wrap text-center">
          <span className="w-2 h-2 rounded-full bg-white animate-pulse" />
          <Radio className="w-5 h-5" />
          <span className="font-semibold text-sm sm:text-base">LIVE NOW</span>
          <span className="text-sm sm:text-base font-medium truncate max-w-xs sm:max-w-md">
            {stream.title || 'Life Church is live'}
          </span>
          <span className="inline-flex items-center gap-1 text-xs sm:text-sm font-semibold bg-white/20 px-2 py-0.5 rounded-full">
            Join us <ArrowRight className="w-3 h-3" />
          </span>
        </div>
      </Link>
    );
  }

  // Upcoming
  const scheduled = new Date(stream.scheduled_at);
  const msUntil = scheduled.getTime() - Date.now();
  const countdown = formatCountdown(msUntil);

  return (
    <Link
      to="/watch-live"
      className="block bg-amber-50 text-amber-900 hover:bg-amber-100 transition border-b border-amber-200"
      data-testid="home-upcoming-banner"
    >
      <div className="container mx-auto px-4 py-2.5 flex items-center justify-center gap-3 flex-wrap text-center">
        <Calendar className="w-5 h-5" />
        <span className="text-sm sm:text-base font-medium">
          Going live in <span className="font-bold tabular-nums">{countdown}</span>
        </span>
        <span className="text-xs sm:text-sm text-amber-800 truncate max-w-xs sm:max-w-md">
          {stream.title || 'Upcoming service'}
        </span>
        <span className="inline-flex items-center gap-1 text-xs font-semibold">
          Set a reminder <ArrowRight className="w-3 h-3" />
        </span>
      </div>
    </Link>
  );
};

export default LiveStreamBanner;
